// How long a post may be shown (D-099): until when, shorten it, and when it runs out, ask the
// creator again or record a new yes. Used on Inbox cards and in "Leaving your walls soon".
import type { ActionResult, Post } from "@brandwall/contracts";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link, useParams } from "react-router";
import { ApiError, patch, post } from "../api";
import { useToast } from "./Toast";

export const formatDay = (d: string) =>
  new Date(`${d}T00:00:00Z`).toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
    timeZone: "UTC",
  });

export function PermissionUntil({ p, canAct }: { p: Post; canAct: boolean }) {
  const { ws } = useParams();
  const qc = useQueryClient();
  const toast = useToast();
  const [open, setOpen] = useState<"shorten" | "record" | null>(null);
  const [shortenTo, setShortenTo] = useState("");
  const [recordUntil, setRecordUntil] = useState("");
  const [givenBy, setGivenBy] = useState("");
  const ended = p.permission === "ended";
  // Only a creator's yes (or a trusted account's post) has an end date.
  if (!p.showUntil || !["granted", "self_granted", "trusted", "ended"].includes(p.permission))
    return null;
  const dayBefore = new Date(Date.parse(`${p.showUntil}T00:00:00Z`) - 86_400_000)
    .toISOString()
    .slice(0, 10);
  const tomorrow = new Date(Date.now() + 86_400_000).toISOString().slice(0, 10);
  const extending = p.request?.purpose === "extend" ? p.request : null;

  const done = async (call: () => Promise<ActionResult>) => {
    try {
      const r = await call();
      toast(r.summary);
      setOpen(null);
      setShortenTo("");
      setRecordUntil("");
      setGivenBy("");
      await Promise.all([
        qc.invalidateQueries({ queryKey: ["posts", ws] }),
        qc.invalidateQueries({ queryKey: ["ending", ws] }),
      ]);
    } catch (err) {
      toast(err instanceof ApiError ? err.message : "That didn't work. Please try again.");
    }
  };
  const extend = () => done(() => post<ActionResult>(`/v1/w/${ws}/posts/${p.id}/extend`));

  return (
    <div className="small stack" style={{ gap: 6 }}>
      <span className={ended ? "" : "muted"}>
        {ended
          ? `Permission ended on ${formatDay(p.showUntil)}: off your walls.`
          : `Shown until ${formatDay(p.showUntil)}.`}
        {extending?.status === "pending" && " Asked to keep showing it."}
      </span>
      {extending?.status === "draft" && extending.needsPerson && (
        <span>
          A message asking to keep showing it is ready.{" "}
          <Link to={`/w/${ws}/rights?r=${extending.id}`}>Send it</Link>
        </span>
      )}
      {canAct && (
        <div className="row wrap" style={{ gap: 8 }}>
          <button type="button" className="btn secondary sm" onClick={extend}>
            Ask again
          </button>
          <button
            type="button"
            className="btn link sm"
            onClick={() => setOpen(open === "record" ? null : "record")}
          >
            Record a new yes
          </button>
          {!ended && (
            <button
              type="button"
              className="btn link sm"
              onClick={() => setOpen(open === "shorten" ? null : "shorten")}
            >
              Shorten
            </button>
          )}
        </div>
      )}
      {open === "shorten" && (
        <form
          className="row wrap"
          style={{ gap: 8 }}
          onSubmit={(e) => {
            e.preventDefault();
            if (shortenTo)
              void done(() =>
                patch<ActionResult>(`/v1/w/${ws}/posts/${p.id}/show-until`, {
                  showUntil: shortenTo,
                }),
              );
          }}
        >
          <input
            className="input"
            type="date"
            aria-label="Show until"
            max={dayBefore}
            value={shortenTo}
            onChange={(e) => setShortenTo(e.target.value)}
          />
          <button type="submit" className="btn sm" disabled={!shortenTo}>
            Save
          </button>
          <span className="muted" style={{ flexBasis: "100%" }}>
            Showing it for longer needs the creator's new yes.
          </span>
        </form>
      )}
      {open === "record" && (
        <form
          className="stack"
          style={{ gap: 6 }}
          onSubmit={(e) => {
            e.preventDefault();
            if (givenBy.trim())
              void done(() =>
                post<ActionResult>(`/v1/w/${ws}/posts/${p.id}/grant`, {
                  givenBy: givenBy.trim(),
                  ...(recordUntil ? { showUntil: recordUntil } : {}),
                }),
              );
          }}
        >
          <input
            className="input"
            aria-label="Who gave the new yes"
            placeholder="Who gave the new yes, and how (e.g. new contract)"
            maxLength={120}
            value={givenBy}
            onChange={(e) => setGivenBy(e.target.value)}
          />
          <label className="row" style={{ gap: 8 }}>
            Until
            <input
              className="input"
              type="date"
              aria-label="New yes until"
              min={tomorrow}
              value={recordUntil}
              onChange={(e) => setRecordUntil(e.target.value)}
            />
            <span className="muted">Empty: your usual time limit.</span>
          </label>
          <button
            type="submit"
            className="btn sm"
            disabled={!givenBy.trim()}
            style={{ justifySelf: "start" }}
          >
            Save the new yes
          </button>
        </form>
      )}
    </div>
  );
}

/** At the top of the Inbox: posts leaving the walls within a week, and those that just did. */
export function LeavingSoon({ posts, canAct }: { posts: Post[]; canAct: boolean }) {
  if (posts.length === 0) return null;
  const soon = posts.filter((p) => p.permission !== "ended").length;
  return (
    <section
      className="card stack"
      aria-label="Leaving your walls soon"
      style={{ marginBottom: 16 }}
    >
      <h2 style={{ margin: 0 }}>
        {soon
          ? `${soon} post${soon === 1 ? " leaves" : "s leave"} your walls within a week`
          : "Posts whose permission ended"}
      </h2>
      <p className="small muted" style={{ margin: 0 }}>
        The creator's yes runs until a date. Ask them again, or record a new yes you got another
        way; a yes puts the post straight back.
      </p>
      {posts.map((p) => (
        <div key={p.id} className="row" style={{ alignItems: "flex-start", gap: 12 }}>
          {p.previewUrl ? (
            <img
              src={p.previewUrl}
              alt=""
              width={56}
              height={70}
              style={{ objectFit: "cover", borderRadius: 8 }}
            />
          ) : null}
          <div className="stack spacer" style={{ gap: 4 }}>
            <b>{p.creator ? `@${p.creator}` : "A post"}</b>
            <PermissionUntil p={p} canAct={canAct} />
          </div>
        </div>
      ))}
    </section>
  );
}
